import React, { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { customFormatMoney, formatMoneyKeepSubunits } from '../../../util/currency';
import { addToCart, buildCartItem } from '../../../util/cart';
import { types as sdkTypes } from '../../../util/sdkLoader';
import {
  describeStock,
  getPriceStockUpdatedAt,
  hasSalesUnit,
  isCompanyListingType,
  priceUnitLine,
} from '../../../util/salesUnit';
import {
  DELIVERY_PICKUP,
  DELIVERY_SHIPPING,
  deliverySubunitsFor,
} from '../../../util/listingOrderSummary';
import { trackListingEvent } from '../../../util/ecommerceEvents';
import {
  MILJOAVGIFT_PER_TYRE_SUBUNITS,
  miljoavgiftLabel,
} from '../../../util/serviceTargetingCore';
import { tireSizePhrase } from '../../../util/answerContentCore';
import { cleanListingTitle } from '../../../util/listingTitle';
import { listingKeyChips } from '../../../util/listingKeyChips';
import NamedLink from '../../../components/NamedLink/NamedLink';
import PaymentMethodsRow from '../../../components/PaymentMethodsRow/PaymentMethodsRow';
import BuyBoxFitCheck from '../BuyBoxFitCheck/BuyBoxFitCheck';
import css from './WheelplaceInfoPanel.module.css';
import { listingTown } from '../../../util/listingTown';

const { Money } = sdkTypes;

const ShieldCheckIcon = () => (
  <svg className={css.trustIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75M21 12c0 5.25-4 8.25-9 9.75C7 20.25 3 17.25 3 12V6l9-3 9 3v6z"
    />
  </svg>
);

const StoreIcon = () => (
  <svg className={css.trustIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72"
    />
  </svg>
);

const formatRelativeDate = (createdAt, intl) => {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;
  const ms = Date.now() - created.getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'Idag';
  if (days === 1) return '1 dag sedan';
  if (days < 14) return `${days} dagar sedan`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks} ${weeks === 1 ? 'vecka' : 'veckor'} sedan`;
  return intl.formatDate(created, { year: 'numeric', month: 'short', day: 'numeric' });
};

/* The size a buyer checks first: the tyre size, or for a bare rim its width
 * and diameter. A staggered set names both axles (P25009-480 BR-8) — the
 * front size alone describes a rear wheel that will not be shipped. */
export const buildDimension = publicData => {
  const { tireWidthFront, profileFront, diameter, rimWidthFront, rimWidthRear } = publicData || {};
  if (tireWidthFront && profileFront && diameter) {
    return tireSizePhrase(publicData);
  }
  // Console enum keys write the decimal point as "_" ("8_5").
  const rimSize = width => `${String(width).replace('_', '.')}x${diameter}"`;
  if (rimWidthFront && diameter) {
    return rimWidthRear && rimWidthRear !== rimWidthFront
      ? `${rimSize(rimWidthFront)} fram / ${rimSize(rimWidthRear)} bak`
      : rimSize(rimWidthFront);
  }
  return diameter ? `${diameter}"` : null;
};

/* What the total line says the delivery part is (BR-6). */
const DELIVERY_WORD = {
  [DELIVERY_SHIPPING]: 'frakt',
  [DELIVERY_PICKUP]: 'upphämtning',
};

/**
 * The buy box (P25009-482). Desktop: the right column. Phone: the buy card
 * directly under the gallery — the same component, placed by the page layout.
 *
 * Top to bottom it answers the three questions a buyer leaves over — does it
 * fit my car, what do I pay in total, how do I get it — and then offers ONE
 * way to buy (BR-7):
 *
 *   title · fit check · price for the chosen quantity · stepper ·
 *   Dimension + Lager · Leverans (one radio choice) · Totalt ·
 *   Köp nu · Lägg i varukorg · "Skicka meddelande" link · two trust lines
 *
 * The quantity, delivery choice and total come from useBuyBoxOrder (the page
 * owns them, because the sticky bar shows the same numbers).
 */
const WheelplaceInfoPanel = props => {
  const {
    listing,
    intl,
    isOwnListing,
    isLiked,
    onToggleFavourite,
    onShare,
    onViewPDF,
    onBuyClick,
    onMessageClick,
    buyLabelId,
    isInquiryProcess,
    sellerCannotTakePayment,
    sellerUsesOwnPayment,
    listingAddress,
    order,
  } = props;

  const [addedToCart, setAddedToCart] = useState(false);

  const { title: rawTitle, price, publicData = {}, createdAt } = listing?.attributes || {};
  /* 18"" → 18" (P25009-486 BR-4). */
  const title = cleanListingTitle(rawTitle);
  /* "Dubb · Nya · 7,5×18 ET42 · 5×108" under the title (BR-4). */
  const keyChips = listingKeyChips(publicData);
  const currency = price?.currency;
  // Öre shown when there are any (BR-6: the total matches checkout to the öre).
  const formatSubunits = amount =>
    currency ? formatMoneyKeepSubunits(intl, new Money(amount, currency)) : '';

  const authorProfile = listing?.author?.attributes?.profile;
  const authorName = authorProfile?.displayName || '';
  const isBusiness = authorProfile?.publicData?.userType === 'business';
  const pickupCity = listingTown(publicData) || '';

  const isWheelProduct = hasSalesUnit(publicData);
  const summary = order?.summary || null;

  // BR-12 (sales unit) — a company listing whose price/stock is synced is alive
  // even when it was created months ago, so it shows when it was last updated.
  const isCompanyListing = isCompanyListingType(publicData.listingType);
  const priceStockUpdatedAt = getPriceStockUpdatedAt(publicData);
  const freshnessLine =
    isCompanyListing && priceStockUpdatedAt
      ? `Uppdaterad ${intl.formatDate(priceStockUpdatedAt, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}`
      : isCompanyListing
        ? null
        : formatRelativeDate(createdAt, intl);

  // Stock is the Sharetribe quantity; only company listings track it.
  const currentStock = listing?.currentStock?.attributes?.quantity;
  const stockRow = isCompanyListing ? describeStock(currentStock, publicData) : null;
  // `=== 0`, not falsy: a listing that never tracked stock must stay buyable.
  const isSoldOut = currentStock === 0;

  /* The order controls (stepper, delivery, total, cart) belong to a real
   * purchase. An inquiry listing, the seller's own ad, a sold-out one and a
   * seller who cannot take payment keep the price and the one action. */
  const canOrder =
    !!summary && isWheelProduct && !isInquiryProcess && !isOwnListing && !isSoldOut && !sellerCannotTakePayment;

  /* ---- BR-1: the price for the chosen quantity ---- */
  const wheels = summary?.wheels || 0;
  const noun = summary?.noun;
  const priceHeadline =
    summary && isWheelProduct
      ? formatSubunits(summary.itemsSubunits)
      : price
        ? customFormatMoney(intl, price)
        : null;
  const priceFor =
    summary && isWheelProduct && noun ? `för ${wheels} ${wheels === 1 ? noun.one : noun.many}` : null;
  const pieceLine =
    summary && isWheelProduct
      ? `${formatSubunits(summary.pieceAmount)}/st${summary.hasVat ? ' · inkl. moms' : ''}`
      : price && isWheelProduct
        ? priceUnitLine({
            unitAmount: price.amount,
            publicData,
            formatAmount: amount => formatSubunits(amount),
          })
        : null;

  const dimension = buildDimension(publicData);
  const specs = [
    /* A staggered size is one line per axle: on one line the value is cut
     * off with "…" in the narrow buy box, and the hidden part is "bak". */
    dimension && { label: 'Dimension', value: dimension, lines: dimension.split(' / ') },
    stockRow && { label: stockRow.label, value: stockRow.value, level: stockRow.level },
  ].filter(Boolean);

  /* ---- BR-5: one delivery choice, priced for this quantity ----
   * P25009-484 BR-4: Hemleverans and "Hämta hos {säljare}" with the place to
   * collect from. Montering is a service now, not a way to get the goods. */
  const deliveryLabel = option =>
    option.key === DELIVERY_SHIPPING ? 'Hemleverans' : `Hämta hos ${authorName || 'säljaren'}`;
  const deliverySubline = option =>
    option.key === DELIVERY_PICKUP ? listingAddress || pickupCity || null : null;
  const deliveryPrice = option => {
    const isChosen = order?.option?.key === option.key;
    const amount =
      isChosen && summary?.confirmed
        ? summary.deliverySubunits
        : deliverySubunitsFor({ option, publicData, quantity: order?.quantity || 1 });
    if (amount === 0) return option.key === DELIVERY_SHIPPING ? 'Fri frakt' : '0 kr';
    return formatSubunits(amount);
  };

  /* ---- BR-6: the total, and what it includes ----
   * P25009-484: the services checkout adds by itself (montering on new
   * complete wheels) and the miljöavgift on new tyres are in the total, each
   * on its own row so the buyer sees what they pay for. A service row is
   * priced by the same function the server charges with (serviceSubunits). */
  const extraRows = summary
    ? [
        ...(summary.services || []).map(s => ({
          key: `service-${s.id}`,
          label: `${s.title || 'Montering'} (ingår)`,
          amount: s.subunits,
        })),
        summary.miljoavgiftSubunits > 0
          ? {
              key: 'miljoavgift',
              label: miljoavgiftLabel(summary.miljoavgiftSubunits / MILJOAVGIFT_PER_TYRE_SUBUNITS),
              amount: summary.miljoavgiftSubunits,
            }
          : null,
      ].filter(Boolean)
    : [];
  const deliveryWord = order?.option ? DELIVERY_WORD[order.option.key] : null;
  /* "inkl. moms (varav moms …), frakt, montering och miljöavgift" — every
   * part of the total besides the goods (P25009-484 BR-5 / BR-6). */
  const included = summary
    ? [
        summary.hasVat && summary.vatSubunits > 0
          ? `moms (varav moms ${formatSubunits(summary.vatSubunits)})`
          : null,
        deliveryWord,
        summary.servicesSubunits > 0 ? 'montering' : null,
        summary.miljoavgiftSubunits > 0 ? 'miljöavgift' : null,
      ].filter(Boolean)
    : [];
  const totalIncludes =
    included.length > 1
      ? `inkl. ${included.slice(0, -1).join(', ')} och ${included[included.length - 1]}`
      : included.length === 1
        ? `inkl. ${included[0]}`
        : '';

  const handleAddToCart = () => {
    /* The stepper is now the hint the old one-unit default lacked: the cart
     * line carries exactly the quantity and delivery the buyer chose here. */
    addToCart(buildCartItem(listing, order?.orderData || {}));
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2200);
  };

  const handleMessage = () => {
    trackListingEvent('message_seller', { listing_id: listing?.id?.uuid, surface: 'buy_box' });
    onMessageClick();
  };

  // Buyer protection is escrow: not for inquiry-only listings, and not when
  // the money does not pass through Wheelplace — a seller who cannot take
  // payment yet (P25009-452) or one who uses their own (P25009-476).
  const showBuyerProtection =
    !isInquiryProcess && !sellerCannotTakePayment && !sellerUsesOwnPayment;

  const resolvedBuyLabelId =
    buyLabelId || (isInquiryProcess ? 'InquiryForm.submitButtonText' : 'ListingPage.orderTitle');
  const showMessageLink = !isInquiryProcess && publicData.allowMessages !== false;

  return (
    <aside className={css.root} aria-label="Köp">
      <div className={css.titleBlock}>
        <h1 className={css.title}>{title}</h1>
        {freshnessLine ? <p className={css.date}>{freshnessLine}</p> : null}
        {keyChips.length > 0 ? (
          <ul className={css.keyChips} aria-label="Nyckeldata">
            {keyChips.map(chip => (
              <li key={chip} className={css.keyChip}>
                {chip}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* BR-3 — the fit check opens the buy box. Only products a car wears. */}
      {isWheelProduct ? <BuyBoxFitCheck listing={listing} /> : null}

      {priceHeadline ? (
        <div className={css.priceBlock}>
          <p className={css.price}>
            {priceHeadline}
            {priceFor ? <span className={css.priceFor}> {priceFor}</span> : null}
          </p>
          {pieceLine ? <p className={css.priceUnitLine}>{pieceLine}</p> : null}
        </div>
      ) : null}

      {/* BR-2 — the quantity stepper. A set listing sells one set. */}
      {canOrder ? (
        <div className={css.quantityRow}>
          <span className={css.quantityLabel}>Antal</span>
          {order.bounds.locked ? (
            <span className={css.quantityLocked}>
              1 sats · {wheels} {noun?.many}
            </span>
          ) : (
            <div className={css.stepper}>
              <button
                type="button"
                className={css.stepperButton}
                onClick={() => order.setQuantity(order.quantity - 1)}
                disabled={order.quantity <= order.bounds.min}
                aria-label="Minska antal"
              >
                −
              </button>
              <span className={css.stepperValue} aria-live="polite">
                {order.quantity}
              </span>
              <button
                type="button"
                className={css.stepperButton}
                onClick={() => order.setQuantity(order.quantity + 1)}
                disabled={order.quantity >= order.bounds.max}
                aria-label="Öka antal"
              >
                +
              </button>
              <span className={css.stepperUnit}>
                {order.quantity === 1 ? noun?.one : noun?.many}
              </span>
            </div>
          )}
        </div>
      ) : null}

      {specs.length > 0 ? (
        <dl className={css.specs}>
          {specs.map(spec => (
            <div key={spec.label} className={css.specRow}>
              <dt className={css.specLabel}>{spec.label}</dt>
              <dd
                className={classNames(css.specValue, {
                  [css.stockOut]: spec.level === 'out',
                  [css.stockLow]: spec.level === 'low',
                  [css.stockIn]: spec.level === 'in',
                })}
              >
                {spec.lines && spec.lines.length > 1
                  ? spec.lines.map(line => (
                      <span key={line} className={css.specLine}>
                        {line}
                      </span>
                    ))
                  : spec.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {canOrder && order.options.length > 0 ? (
        <fieldset className={css.delivery}>
          <legend className={css.deliveryLegend}>Leverans</legend>
          {order.options.map(option => {
            const checked = order.option?.key === option.key;
            const subline = deliverySubline(option);
            return (
              <label
                key={option.key}
                className={classNames(css.deliveryOption, { [css.deliveryOptionOn]: checked })}
              >
                <input
                  type="radio"
                  className={css.deliveryRadio}
                  name={`delivery-${listing?.id?.uuid}`}
                  value={option.key}
                  checked={checked}
                  onChange={() => order.setOptionKey(option.key)}
                />
                <span className={css.deliveryText}>
                  <span className={css.deliveryName}>{deliveryLabel(option)}</span>
                  {subline ? (
                    <span className={css.deliverySub} title={subline}>
                      {subline}
                    </span>
                  ) : null}
                </span>
                <span className={css.deliveryPrice}>{deliveryPrice(option)}</span>
              </label>
            );
          })}
        </fieldset>
      ) : null}

      {canOrder ? (
        <div className={css.total}>
          {extraRows.map(row => (
            <div key={row.key} className={css.extraRow}>
              <span>{row.label}</span>
              <span>{formatSubunits(row.amount || 0)}</span>
            </div>
          ))}
          <div className={css.totalRow}>
            <span className={css.totalLabel}>Totalt</span>
            <span className={css.totalValue}>{formatSubunits(summary.totalSubunits)}</span>
          </div>
          {totalIncludes ? <p className={css.totalIncludes}>{totalIncludes}</p> : null}
        </div>
      ) : null}

      {sellerCannotTakePayment ? (
        /* P25009-452 BR-1 — this seller cannot yet take payment through the
         * platform, so the ad must not promise "Köp nu". Contacting the seller
         * is the ONE primary action.
         *
         * P25009-476 BR-1 — the line under it says WHY. It used to read
         * "Säljaren hanterar betalningen direkt", which is the own-payment
         * wording, so this state looked like a seller who handles payment
         * themselves. The two states now read differently. */
        <div className={css.ctaGroup}>
          <button
            type="button"
            className={css.buy}
            onClick={onMessageClick}
            disabled={isOwnListing}
          >
            <FormattedMessage id="ListingPage.contactSellerButton" defaultMessage="Kontakta säljaren" />
          </button>
          <p className={css.directPaymentNote}>
            <FormattedMessage id="ListingPage.sellerCannotTakePaymentYet" />
          </p>
        </div>
      ) : (
        <div className={css.ctaGroup}>
          {/* BR-7 — Köp nu is the one primary action and always first. On a
            * phone it lives in the sticky bar instead (see the CSS). */}
          <button
            type="button"
            className={css.buy}
            onClick={() => onBuyClick(order?.orderData || {})}
            disabled={isOwnListing || isSoldOut}
          >
            {isSoldOut ? (
              'Slutsåld'
            ) : (
              <FormattedMessage
                id={resolvedBuyLabelId}
                defaultMessage={isInquiryProcess ? 'Skicka förfrågan' : 'Köp nu'}
              />
            )}
          </button>
          {canOrder ? (
            <button type="button" className={css.addToCart} onClick={handleAddToCart}>
              {addedToCart ? 'Tillagd i varukorgen' : 'Lägg i varukorg'}
            </button>
          ) : null}
          {showMessageLink ? (
            <button
              type="button"
              className={css.messageLink}
              onClick={handleMessage}
              disabled={isOwnListing}
            >
              Frågor? Skicka meddelande till säljaren
            </button>
          ) : null}
          {sellerUsesOwnPayment ? (
            /* P25009-476 — the own-payment wording, on the own-payment state:
             * the buyer orders here and pays through the seller's link. */
            <p className={css.directPaymentNote}>
              <FormattedMessage id="ListingPage.ownPaymentNote" />
            </p>
          ) : null}
          {/* BR-4 — how the buyer can pay, before the click. Only when the
            * money goes through Wheelplace. */}
          {canOrder && !sellerUsesOwnPayment ? (
            <PaymentMethodsRow className={css.paymentMethods} currency={currency} />
          ) : null}
        </div>
      )}

      {/* BR-8 — two trust lines. */}
      <ul className={css.trust}>
        {showBuyerProtection ? (
          <li className={css.trustItem}>
            <ShieldCheckIcon />
            <span className={css.trustText}>
              Köpskydd — pengarna hålls tills du godkänt leveransen.{' '}
              <NamedLink name="CMSPage" params={{ pageId: 'kopskydd' }} className={css.trustLink}>
                Så fungerar det
              </NamedLink>
            </span>
          </li>
        ) : null}
        {showBuyerProtection ? (
          <li className={css.trustItem}>
            <ShieldCheckIcon />
            <span className={css.trustText}>30 dagars reklamationsrätt</span>
          </li>
        ) : null}
        {authorName ? (
          <li className={css.trustItem}>
            <StoreIcon />
            <span className={css.trustText}>
              Säljs av <strong>{authorName}</strong> ·{' '}
              {isBusiness ? 'verifierad företagssäljare' : 'privatperson'}
            </span>
          </li>
        ) : null}
      </ul>

      <div className={css.actionRow}>
        <button type="button" className={css.actionBtn} onClick={onShare}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z"
            />
          </svg>
          Dela
        </button>
        <button type="button" className={css.actionBtn} onClick={onViewPDF}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
            />
          </svg>
          Visa PDF
        </button>
        {!isOwnListing && onToggleFavourite ? (
          <button
            type="button"
            className={classNames(css.actionBtn, css.actionBtnIcon, {
              [css.actionBtnIconLiked]: isLiked,
            })}
            onClick={onToggleFavourite}
            aria-label={isLiked ? 'Ta bort från favoriter' : 'Lägg till i favoriter'}
          >
            <svg
              fill={isLiked ? 'currentColor' : 'none'}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
              />
            </svg>
          </button>
        ) : null}
      </div>
    </aside>
  );
};

export default WheelplaceInfoPanel;
